import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { 
  Paper, 
  TextField, 
  IconButton, 
  Typography, 
  Box, 
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { chatService } from '../../services/chatService';
import { websocketService, ConnectionStatus } from '../../services/websocketService';
import { ChatMessage, ChatSession, ChatCompletionRequest } from '../../types/api.types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Custom high-contrast style for better readability
const customHighContrastStyle = {
  ...prism,
  'code[class*="language-"]': {
    ...prism['code[class*="language-"]'],
    color: '#000000', // Black text
    background: '#ffffff', // White background
  },
  'pre[class*="language-"]': {
    ...prism['pre[class*="language-"]'],
    color: '#000000', // Black text
    background: '#f8f9fa', // Light gray background
    border: '1px solid #e9ecef',
    borderRadius: '4px',
  },
  'token.comment': {
    color: '#6a737d', // Gray for comments
  },
  'token.string': {
    color: '#032f62', // Dark blue for strings
  },
  'token.number': {
    color: '#005cc5', // Blue for numbers
  },
  'token.boolean': {
    color: '#d73a49', // Red for booleans
  },
  'token.keyword': {
    color: '#d73a49', // Red for keywords
  },
  'token.function': {
    color: '#6f42c1', // Purple for functions
  },
  'token.property': {
    color: '#005cc5', // Blue for properties
  },
  'token.punctuation': {
    color: '#24292e', // Dark gray for punctuation
  },
};

const ChatContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'rightPanelWidth',
})<{ rightPanelWidth: number }>(({ theme, rightPanelWidth }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  flexGrow: 1,
  borderRadius: 0,
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  border: 'none',
  width: `calc(100% - 250px - ${rightPanelWidth}px)`, // 250px is sidebar width
  transition: 'width 0.3s ease',
  position: 'relative',
  boxSizing: 'border-box',
  overflow: 'hidden', // Prevent any content from overflowing
}));

const ChatHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}));

const MessagesContainer = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  padding: theme.spacing(2, 3),
  backgroundColor: '#f8f9fa',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  width: '100%',
  minHeight: 0, // Important for proper scrolling in a flex container
  gap: theme.spacing(1), // Add consistent spacing between messages
  '&::-webkit-scrollbar': {
    width: '16px',
    position: 'absolute',
    right: 0,
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '8px',
    margin: theme.spacing(1, 0),
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '8px',
    border: '4px solid #f8f9fa',
    minHeight: '100px',
    '&:hover': {
      background: '#a1a1a1',
    },
  },
}));

const InputContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#ffffff',
  alignItems: 'center',
  gap: theme.spacing(1),
  boxSizing: 'border-box',
  boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
}));

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  padding: theme.spacing(2, 3),
  maxWidth: '70%',
  marginBottom: theme.spacing(2),
  backgroundColor: isUser ? '#007bff' : '#ffffff',
  color: isUser ? '#ffffff' : '#2c3e50',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  borderRadius: theme.spacing(2),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  wordBreak: 'break-word',
  '& p': {
    margin: 0,
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
  },
  '& ul, & ol': {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& code': {
    backgroundColor: isUser ? 'rgba(255, 255, 255, 0.2)' : '#f1f3f4',
    color: isUser ? '#ffffff' : '#e91e63',
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.spacing(0.5),
    fontSize: '0.875em',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: '1',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#F9FAFB',
    backgroundColor: '#f8f9fa',
    color: '#000000ff',
    minHeight: '48px',
    maxHeight: '120px',
    overflowY: 'auto',
    width: '100%',
    boxSizing: 'border-box',
    '& fieldset': {
      border: '3px solid #2463EB',
    },
    '&:hover fieldset': {
      border: '3px solid #2463EB',
    },
    '&.Mui-focused fieldset': {
      border: '3px solid #2463EB',
    },
    '& textarea': {
      resize: 'none',
      maxHeight: '100px',
      overflowY: 'auto',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      '&::-webkit-scrollbar': {
        width: '12px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '6px',
        border: '3px solid #f8f9fa',
        minHeight: '40px',
        '&:hover': {
          background: '#a1a1a1',
        },
      },
    },
  },
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  color: '#666666',
  borderRadius: '50%',
  width: 48,
  height: 48,
  flexShrink: 0,
  marginTop: '0',
  alignSelf: 'center',
  '&:hover': {
  },
  '&:disabled': {
    color: '#666666',
  },
}));

const TypingIndicator = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  alignSelf: 'flex-start',
  marginBottom: theme.spacing(1),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
}));

const LoadingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

interface Message {
  _id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  created_at: string;
}

interface ChatInterfaceProps {
  selectedProject: string | null;
  rightPanelWidth?: number;
}

const ChatInterface = forwardRef<any, ChatInterfaceProps>(({ selectedProject, rightPanelWidth = 300 }, ref) => {
  const [input, setInput] = useState('');
  const [displayInput, setDisplayInput] = useState(''); // Immediate UI update
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<boolean>(false);
  const [typing, setTyping] = useState<boolean>(false);
  const [streamResponse, setStreamResponse] = useState<string>('');
  const [lastMessageCount, setLastMessageCount] = useState<number>(0);
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Helper function to safely set typing state with timeout protection
  const setTypingWithTimeout = (isTyping: boolean, timeoutMs: number = 30000) => {
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    setTyping(isTyping);

    if (isTyping) {
      // Set a safety timeout to prevent stuck typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        console.warn('Typing timeout reached - forcing typing state to false');
        setTyping(false);
        setStreamResponse('');
        // Try to fetch messages as a fallback
        if (selectedSession) {
          fetchMessages();
        }
      }, timeoutMs);
    }
  };

  // Clear typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);
  
  // Periodic check for new messages (useful for scan completion detection)
  useEffect(() => {
    if (selectedProject && selectedSession && !typing && !sending) {
      // Set up periodic refresh every 10 seconds when not actively chatting
      refreshIntervalRef.current = setInterval(() => {
        checkForNewMessages();
      }, 10000);
    } else {
      // Clear interval when actively chatting or no session
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [selectedProject, selectedSession, typing, sending]);
  
  // Check for new messages without disrupting the UI
  const checkForNewMessages = async () => {
    if (!selectedSession || typing || sending) return;
    
    try {
      const response = await chatService.getMessages(selectedSession);
      
      // Replace token expiration error messages with user-friendly versions
      const processedResponse: Message[] = response.map(m => {
        const contentLower = (m.content || '').toLowerCase();
        if (contentLower.includes('expiredtokenexception') || 
            contentLower.includes('security token included in the request is expired') ||
            (contentLower.includes('security token') && contentLower.includes('expired'))) {
          return {
            _id: m._id,
            content: `Error - Token is expired, please try login again`,
            role: m.role,
            created_at: m.created_at
          } as Message;
        }
        return {
          _id: m._id,
          content: m.content,
          role: m.role,
          created_at: m.created_at
        } as Message;
      });
      
      if (processedResponse.length > lastMessageCount) {
        console.log('New messages detected, updating chat...');
        // Only enable auto-scroll if user was already near bottom
        const wasNearBottom = isNearBottom();
        setMessages(processedResponse);
        setLastMessageCount(processedResponse.length);
        if (wasNearBottom) {
          setShouldAutoScroll(true);
        }
      }
    } catch (err) {
      // Silently fail for background checks
      console.log('Background message check failed:', err);
    }
  };
  
  // Update message count when messages change
  useEffect(() => {
    setLastMessageCount(messages.length);
  }, [messages]);
  
  // Get or create a session when project changes
  useEffect(() => {
    // First, clear existing session data
    setSessions([]);
    setMessages([]);
    setSelectedSession(null);
    setStreamResponse('');
    setTyping(false);
    setSending(false);
    
    // Then, if a project is selected, get or create its session
    if (selectedProject) {
      getOrCreateProjectSession();
    }
  }, [selectedProject]);
  
  // Connection status state
  const [connectionState, setConnectionState] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  
  // Connect to chat WebSocket when session is selected
  useEffect(() => {
    if (selectedProject && selectedSession) {
      // Define WebSocket callbacks
      const callbacks = {
        onChatStream: (data: { chunk: string; is_final: boolean }) => {
          if (data.chunk) {
            // Update stream response with the chunk
            setStreamResponse(prev => prev + data.chunk);
          }
          
          // When stream is complete, update typing state and fetch messages
          if (data.is_final) {
            setTypingWithTimeout(false);
            setStreamResponse(''); // Clear stream response
            // Add a small delay to ensure backend has finished processing
            setTimeout(() => {
              fetchMessages();
            }, 500);
          }
        },
        onChatMessageAdded: (message: any) => {
          console.log('New chat message added via WebSocket:', message);
          // Stop typing immediately when a new message is received
          setTypingWithTimeout(false);
          setStreamResponse('');
          // Refresh messages to show the new message
          fetchMessages();
        },
        onConnectionStatusChange: (status: ConnectionStatus) => {
          console.log('Chat WebSocket connection status changed:', status);
          setConnectionState(status);
          
          // Reset reconnection counter when connected
          if (status === ConnectionStatus.CONNECTED) {
            setReconnectAttempt(0);
          } else if (status === ConnectionStatus.RECONNECTING) {
            setReconnectAttempt(prev => prev + 1);
          }
          
          // Show non-intrusive status message for reconnection attempts
          if (status === ConnectionStatus.RECONNECTING) {
            // Show a temporary message that will auto-hide
            const tempMessage = `Reconnecting to chat server (attempt ${reconnectAttempt + 1})...`;
            setError(tempMessage);
            
            // Auto-hide after 3 seconds
            setTimeout(() => {
              if (error === tempMessage) {
                setError(null);
              }
            }, 3000);
          } else if (status === ConnectionStatus.CONNECTED && reconnectAttempt > 0) {
            // Show success message when reconnected
            const successMessage = 'Successfully reconnected to chat server!';
            setError(successMessage);
            
            // Auto-hide after 3 seconds
            setTimeout(() => {
              if (error === successMessage) {
                setError(null);
              }
            }, 3000);
          }
        },
        onError: (error: any) => {
          console.error('Chat WebSocket error:', error);
          // Display a more user-friendly error message
          if (error.fallback) {
            console.log('Using fallback mechanism:', error.fallback);
            // Show a less alarming message for fallback mechanisms
            const fallbackMessage = `Using alternative connection method. ${error.fallback}`;
            setError(fallbackMessage);
            
            // Auto-hide after 5 seconds
            setTimeout(() => {
              if (error === fallbackMessage) {
                setError(null);
              }
            }, 5000);
          } else {
            // For actual errors, show a more detailed message with retry button
            setError(`Connection error: ${error.detail || 'Unknown error'}. Try sending another message or reload the page.`);
          }
        }
      };
      
      // Connect to chat WebSocket
      websocketService.connectToChat(selectedProject, selectedSession, callbacks);
    }
    
    return () => {
      // Disconnect when component unmounts or session changes
      if (selectedProject && selectedSession) {
        websocketService.disconnect();
      }
    };
  }, [selectedProject, selectedSession]);
  
  // Function to manually reconnect WebSocket
  const handleReconnect = useCallback(() => {
    if (selectedProject && selectedSession) {
      setError('Attempting to reconnect...');
      websocketService.reconnectToChat();
    }
  }, [selectedProject, selectedSession]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, typing]);
  
  // Create a new stream response message when streaming starts
  useEffect(() => {
    if (typing && streamResponse) {
      // Create a temporary message for the stream response
      const tempMessage: Message = {
        _id: 'stream-response',
        content: streamResponse,
        role: 'assistant',
        created_at: new Date().toISOString(),
      };
      
      // Find if we already have a temporary message
      const tempIndex = messages.findIndex(m => m._id === 'stream-response');
      
      if (tempIndex >= 0) {
        // Update the temporary message
        const updatedMessages = [...messages];
        updatedMessages[tempIndex] = tempMessage;
        setMessages(updatedMessages);
      } else {
        // Add a new temporary message
        setMessages([...messages, tempMessage]);
      }
    } else if (!typing && streamResponse) {
      // If typing stopped but we still have stream response, clean it up
      setStreamResponse('');
      setMessages(prevMessages => prevMessages.filter(m => m._id !== 'stream-response'));
    }
  }, [streamResponse, typing]);
  
  // Check if user is near the bottom of the chat
  const isNearBottom = useCallback(() => {
    if (!chatContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const threshold = 100; // pixels from bottom
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  // Handle scroll events to determine if auto-scroll should be enabled
  const handleScroll = useCallback(() => {
    const nearBottom = isNearBottom();
    setShouldAutoScroll(nearBottom);
  }, [isNearBottom]);

  const scrollToBottom = () => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Get or create a single chat session for the project
  const getOrCreateProjectSession = async () => {
    if (!selectedProject) return;
    
    console.log('🔄 Creating session for project:', selectedProject);
    
    try {
      setLoading(true);
      // Use the new method to get or create a single session
      const session = await chatService.getOrCreateProjectSession(selectedProject);
      
      console.log('✅ Session created/retrieved:', session);
      
      // Set the session and update state
      setSessions([session]);
      setSelectedSession(session._id);
      
      // Fetch messages for this session
      await fetchMessages(session._id);
    } catch (err) {
      console.error('Failed to get or create project chat session:', err);
      setError('Failed to load chat. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Debounced input update to prevent excessive re-renders
  const inputTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const handleInputChange = useCallback((value: string) => {
    // Enforce 2000 character limit
    const maxLength = 2000;
    const truncatedValue = value.length > maxLength ? value.substring(0, maxLength) : value;


    // Update display immediately for smooth typing
    setDisplayInput(truncatedValue);
    
    // Debounce the actual input state update
    if (inputTimeoutRef.current) {
      clearTimeout(inputTimeoutRef.current);
    }
    
    inputTimeoutRef.current = setTimeout(() => {
      setInput(truncatedValue);
    }, 100); // 100ms debounce
  }, []);

  // Clean up input timeout
  useEffect(() => {
    return () => {
      if (inputTimeoutRef.current) {
        clearTimeout(inputTimeoutRef.current);
      }
    };
  }, []);

  // Memoized message component to prevent unnecessary re-renders
  const MemoizedMessage = React.memo(({ message }: { message: Message }) => (
    <MessageBubble 
      key={message._id} 
      isUser={message.role === 'user'}
    >
      <Box sx={{ whiteSpace: 'pre-wrap' }}>
        {message.role === 'user' ? (
          <Typography variant="body1">
            {message.content}
          </Typography>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={customHighContrastStyle}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
              a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />,
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </Box>
      <Typography 
        variant="caption" 
        color={message.role === 'user' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'}
      >
        {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Typography>
    </MessageBubble>
  ));

  // Memoize message list to prevent unnecessary re-renders
  const memoizedMessages = useMemo(() => {
    return messages.map((message, index) => (
      <MemoizedMessage key={message._id || index} message={message} />
    ));
  }, [messages]);
  
  // Fetch messages for a session
  const fetchMessages = async (sessionId?: string) => {
    const id = sessionId || selectedSession;
    if (!id) return;
    
    try {
      // Don't show loading spinner for background refreshes
      const isBackgroundRefresh = sessionId === undefined;
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      
      const wasNearBottom = isNearBottom();
      const response = await chatService.getMessages(id);
      
      // Remove any temporary stream messages and replace token expiration errors with user-friendly messages
      const filteredResponse: Message[] = response.map(m => {
        if (m._id === 'stream-response') return null;
        
        // Replace messages containing token expiration errors with user-friendly content
        const contentLower = (m.content || '').toLowerCase();
        if (contentLower.includes('expiredtokenexception') || 
            contentLower.includes('security token included in the request is expired') ||
            (contentLower.includes('security token') && contentLower.includes('expired'))) {
          console.log('Replacing token expiration error message with user-friendly version');
          return {
            _id: m._id,
            content: `Error - Token is expired, please try login again`,
            role: m.role,
            created_at: m.created_at
          } as Message;
        }
        
        return {
          _id: m._id,
          content: m.content,
          role: m.role,
          created_at: m.created_at
        } as Message;
      }).filter((m): m is Message => m !== null);
      
      setMessages(filteredResponse);
      setLastMessageCount(filteredResponse.length);
      
      // Only enable auto-scroll if user was already near bottom (for manual refreshes)
      if (isBackgroundRefresh && wasNearBottom) {
        setShouldAutoScroll(true);
      }
      
      console.log(`Fetched ${filteredResponse.length} messages for session ${id}`);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      // Only show error for foreground fetches
      const isBackgroundRefresh = sessionId === undefined;
      if (!isBackgroundRefresh) {
        setError('Failed to load messages. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sending a message
  const handleSendMessage = () => {
    if (displayInput.trim() && selectedProject && selectedSession) {
      sendMessage(displayInput.trim());
    }
  };
  
  // Send a message to the API
  const sendMessage = async (content: string) => {
    if (!selectedProject || !selectedSession) {
      setError('Please select a project and ensure a session is created first.');
      return;
    }
    
    // Check connection status before attempting to send
    if (connectionState === ConnectionStatus.ERROR) {
      // If connection has error, attempt to reconnect first
      handleReconnect();
      // Wait a bit to see if connection is restored
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // Validate project ID format (should contain only alphanumeric, hyphens, underscores)
    const projectIdRegex = /^[a-zA-Z0-9_-]+$/;
    if (!projectIdRegex.test(selectedProject)) {
      setError(`Invalid project ID format: "${selectedProject}". Only letters, numbers, hyphens, and underscores are allowed.`);
      return;
    }
    
    // Validate session ID format
    if (!projectIdRegex.test(selectedSession)) {
      setError(`Invalid session ID format: "${selectedSession}". Only letters, numbers, hyphens, and underscores are allowed.`);
      return;
    }
    
    console.log('Sending message with project_id:', selectedProject, 'session_id:', selectedSession);
    
    try {
      setSending(true);
      setTyping(false);
      setStreamResponse('');
      
      // Add user message to the UI immediately
      const userMessage: Message = {
        _id: Date.now().toString(),
        content: content,
        role: 'user',
        created_at: new Date().toISOString(),
      };
      
      setMessages([...messages, userMessage]);
      setInput('');
      setDisplayInput('');
      
      // Enable auto-scroll when user sends a message
      setShouldAutoScroll(true);
      
      // Prepare the request
      const request: ChatCompletionRequest = {
        project_id: selectedProject,
        session_id: selectedSession,
        message: content,
      };
      
      // Debug logging
      console.log('🔍 Chat Request Debug:', {
        selectedProject,
        selectedSession,
        content,
        connectionState,
        projectValid: selectedProject ? /^[a-zA-Z0-9_-]+$/.test(selectedProject) : false,
        sessionValid: selectedSession ? /^[a-zA-Z0-9_-]+$/.test(selectedSession) : false,
        projectLength: selectedProject?.length || 0,
        sessionLength: selectedSession?.length || 0,
        projectType: typeof selectedProject,
        sessionType: typeof selectedSession,
        request
      });
      
      // Start streaming response
      setTypingWithTimeout(true, 60000); // 60 second timeout for report generation
      
      // Use streaming API to get the response
      const closeStream = chatService.sendMessageStreaming(
        request,
        (chunk) => {
          // Check if the chunk contains token expiration error BEFORE accumulating
          const chunkLower = chunk.toLowerCase();
          if (chunkLower.includes('expiredtokenexception') || 
              chunkLower.includes('security token included in the request is expired') ||
              (chunkLower.includes('security token') && chunkLower.includes('expired'))) {
            console.log('Token expiration detected in stream chunk:', chunk);
            
            // Immediately stop typing and clear stream response
            setTypingWithTimeout(false);
            setStreamResponse('');
            
            // Immediately display the error message
            setError('Token is expired, please try login again');
            
            // Add the error message to chat immediately
            const errorMessage: Message = {
              _id: `error-${Date.now()}`,
              content: 'Error - Token is expired, please try login again',
              role: 'assistant',
              created_at: new Date().toISOString(),
            };
            setMessages(prevMessages => [...prevMessages.filter(m => m._id !== 'stream-response'), errorMessage]);
            
            return; // Don't accumulate the original error message
          }
          
          // Only accumulate non-error chunks
          setStreamResponse(prev => prev + chunk);
        },
        () => {
          // When the stream is done, clean up and fetch messages
          setTypingWithTimeout(false);
          setStreamResponse(''); // Clear the stream response
          
          // Remove any temporary stream message before fetching real messages
          setMessages(prevMessages => prevMessages.filter(m => m._id !== 'stream-response'));
          
          // Add a delay to ensure backend has processed everything
          setTimeout(() => {
            fetchMessages();
          }, 500); // Reduced delay for faster response display
        },
        (error) => {
          console.error('Error streaming response:', error);
          console.log('Error object structure:', {
            status: error.status,
            message: error.message,
            response: error.response,
            data: error.data,
            fullError: error
          });
          
          // More detailed error handling based on error type
          const errorMessage = (error.message || '').toLowerCase();
          const isTokenExpired = error.status === 401 || 
            errorMessage.includes('expiredtokenexception') || 
            errorMessage.includes('expired') || 
            errorMessage.includes('401') ||
            errorMessage.includes('security token') ||
            errorMessage.includes('token is expired') ||
            errorMessage.includes('unauthorized');
            
          console.log('Token expiration check:', {
            errorMessage,
            isTokenExpired,
            checks: {
              status401: error.status === 401,
              hasExpiredToken: errorMessage.includes('expiredtokenexception'),
              hasExpired: errorMessage.includes('expired'),
              has401: errorMessage.includes('401'),
              hasSecurityToken: errorMessage.includes('security token'),
              hasTokenExpired: errorMessage.includes('token is expired'),
              hasUnauthorized: errorMessage.includes('unauthorized')
            }
          });
            
          if (isTokenExpired) {
            // Immediately stop typing and display error
            setTypingWithTimeout(false);
            setStreamResponse('');
            setError('Token is expired, please try login again');
            
            // Add error message to chat immediately
            const errorMessage: Message = {
              _id: `error-${Date.now()}`,
              content: 'Error - Token is expired, please try login again',
              role: 'assistant',
              created_at: new Date().toISOString(),
            };
            setMessages(prevMessages => [...prevMessages.filter(m => m._id !== 'stream-response'), errorMessage]);
          } else if (error.message && error.message.includes('network')) {
            setError('Network error. Please check your connection and try again.');
          } else if (error.status === 403) {
            setError('Authorization error. Please reload the page and try again.');
          } else {
            setError(`Failed to get response: ${error.message || 'Unknown error'}. Please try again.`);
          }
          
          setTypingWithTimeout(false);
        }
      );
    } catch (err: any) {
      console.error('Failed to send message:', err);
      console.log('Catch block error structure:', {
        status: err.status,
        message: err.message,
        response: err.response,
        responseStatus: err.response?.status,
        responseData: err.response?.data,
        fullError: err
      });
      
      // Check for token expiration errors
      const errorMessage = (err.message || '').toLowerCase();
      const isTokenExpired = err.response?.status === 401 || 
        errorMessage.includes('expiredtokenexception') || 
        errorMessage.includes('expired') || 
        errorMessage.includes('401') ||
        errorMessage.includes('security token') ||
        errorMessage.includes('token is expired') ||
        errorMessage.includes('unauthorized');
        
      console.log('Catch block token expiration check:', {
        errorMessage,
        isTokenExpired,
        responseStatus: err.response?.status
      });
        
      if (isTokenExpired) {
        // Immediately stop typing and display error
        setTypingWithTimeout(false);
        setStreamResponse('');
        setError('Token is expired, please try login again');
        
        // Add error message to chat immediately
        const errorMessage: Message = {
          _id: `error-${Date.now()}`,
          content: 'Error - Token is expired, please try login again',
          role: 'assistant',
          created_at: new Date().toISOString(),
        };
        setMessages(prevMessages => [...prevMessages.filter(m => m._id !== 'stream-response'), errorMessage]);
      } else {
        setError('Failed to send message. Please try again.');
      }
      
      setTypingWithTimeout(false);
    } finally {
      setSending(false);
    }
  };
  
  // Handle pressing Enter to send message
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Handle menu open
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle error message close
  const handleCloseError = () => {
    setError(null);
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    // Method to get the current session ID
    getCurrentSession: () => selectedSession,
    
    // Method to add a message to the chat
    addMessage: (message: string, isSystem: boolean = false) => {
      const newMessage: Message = {
        _id: Date.now().toString(),
        content: message,
        role: isSystem ? 'system' : 'assistant',
        created_at: new Date().toISOString(),
      };
      setMessages(prevMessages => [...prevMessages, newMessage]);
    }
  }));
  
  return (
    <ChatContainer elevation={0} rightPanelWidth={rightPanelWidth}>
      {!selectedProject ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Typography variant="h6" color="#666666">
            Select a project to start chatting
          </Typography>
        </Box>
      ) : (
        <>
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'medium', color: '#000000' }}>
                Project Chat
              </Typography>
            </Box>
            <IconButton onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => { 
                fetchMessages(); 
                handleMenuClose(); 
              }}>
                Refresh Messages
              </MenuItem>
              <MenuItem onClick={() => { 
                checkForNewMessages(); 
                handleMenuClose(); 
              }}>
                Check for New Messages
              </MenuItem>
            </Menu>
          </ChatHeader>
          
          <MessagesContainer ref={chatContainerRef} onScroll={handleScroll}>
            {loading && messages.length === 0 ? (
              <LoadingContainer>
                <CircularProgress size={24} />
              </LoadingContainer>
            ) : messages.length === 0 ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Typography variant="body1" color="text.secondary">
                  No messages yet. Start the conversation!
                </Typography>
              </Box>
            ) : (
              <>
{memoizedMessages}
                
                {typing && !streamResponse && (
                  <TypingIndicator>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2">AI is typing...</Typography>
                  </TypingIndicator>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </MessagesContainer>
          
          <InputContainer>
            <StyledTextField
              fullWidth
              placeholder="Type your message..."
              variant="outlined"
              size="small"
              value={displayInput}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!selectedProject || !selectedSession || sending || typing}
              multiline
              maxRows={4}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              helperText={`${displayInput.length}/2000 characters`}
            />
            <SendButton 
              onClick={handleSendMessage} 
              disabled={!displayInput.trim() || !selectedProject || !selectedSession || sending || typing}
              sx={{ ml: 1 }}
            >
              {sending ? <CircularProgress size={24} /> : <SendIcon />}
            </SendButton>
          </InputContainer>
        </>
      )}
      
      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        action={
          connectionState === ConnectionStatus.ERROR && (
            <Button color="secondary" size="small" onClick={handleReconnect}>
              Reconnect
            </Button>
          )
        }
      >
        <Alert 
          onClose={handleCloseError} 
          severity={error && error.includes('Successfully reconnected') ? "success" : 
                  error && error.includes('Reconnecting') ? "info" : 
                  error && error.includes('alternative connection') ? "warning" : "error"}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
      
    </ChatContainer>
  );
});

export default ChatInterface;
